import type { Language } from '../types/i18n'
import type { FilterKey } from './media'

export type MediaText = {
  categories: Record<FilterKey, string>
  photos: Record<string, { caption: string; alt: string }>
  reels: Record<string, { title: string; meta: string }>
  snippets: Record<string, { title: string; meta: string; description: string }>
}

export const mediaText: Record<Language, MediaText> = {
  nl: {
    categories: { all: 'Alles', portrait: 'Portret', stage: 'Podium', studio: 'Studio' },
    photos: {
      headshot: { caption: 'Casting headshot', alt: 'Casting headshot van Fauve Willems' },
      'portrait-soft': { caption: 'Editorial portret', alt: 'Portret in zachte roze tinten' },
      stage1: {
        caption: 'Eindpresentatie LMIPA',
        alt: 'Fauve springend op een groot podium',
      },
      'dance-studio': { caption: 'Modern · studiowerk', alt: 'Moderne dans in de studio' },
      ballet: { caption: 'Ballet aan de barre', alt: 'Ballet aan de barre' },
      teaching: {
        caption: 'Docent dans, LMIPA',
        alt: 'Fauve geeft dansles aan een groep studenten',
      },
      stage2: { caption: 'Ensemble · arenashow', alt: 'Ensemble op een arenapodium' },
    },
    reels: {
      'reel-dance': { title: 'Commercial / Jazz reel', meta: '0:58 · choreografie' },
      'reel-voice': { title: 'Zangreel · sopraan', meta: '1:12 · musical & pop' },
      'reel-acting': { title: 'Acteerreel · monoloog', meta: '1:04 · theater & camera' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Eindpresentatie LMIPA',
        meta: 'Dance captain · solo',
        description:
          'Fragment uit de jaarlijkse eindpresentatie waarin ik als dance captain verantwoordelijk was voor verschillende stukken.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · nov 2025',
        description:
          'Ensemblewerk uit het Discobal in AFAS Live, met live band en volledige cast.',
      },
      'snippet-teaching': {
        title: 'Choreografie jongere afdeling',
        meta: 'LMIPA · docent dans',
        description:
          'Zelf gemaakte choreografie voor de jongere afdelingen van Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  en: {
    categories: { all: 'All', portrait: 'Portrait', stage: 'Stage', studio: 'Studio' },
    photos: {
      headshot: { caption: 'Casting headshot', alt: 'Casting headshot of Fauve Willems' },
      'portrait-soft': { caption: 'Editorial portrait', alt: 'Portrait in soft pink tones' },
      stage1: { caption: 'LMIPA showcase', alt: 'Fauve leaping on a large stage' },
      'dance-studio': { caption: 'Modern · studio work', alt: 'Modern dance in the studio' },
      ballet: { caption: 'Ballet at the barre', alt: 'Ballet at the barre' },
      teaching: {
        caption: 'Dance teacher, LMIPA',
        alt: 'Fauve teaching a dance class to a group of students',
      },
      stage2: { caption: 'Ensemble · arena show', alt: 'Ensemble on an arena stage' },
    },
    reels: {
      'reel-dance': { title: 'Commercial / jazz reel', meta: '0:58 · choreography' },
      'reel-voice': { title: 'Vocal reel · soprano', meta: '1:12 · musical & pop' },
      'reel-acting': { title: 'Acting reel · monologue', meta: '1:04 · theatre & camera' },
    },
    snippets: {
      'snippet-stage': {
        title: 'LMIPA showcase',
        meta: 'Dance captain · solo',
        description:
          'Excerpt from the annual showcase, where I was dance captain and responsible for several pieces.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · Nov 2025',
        description:
          'Ensemble work from the Discobal at AFAS Live, with a live band and full cast.',
      },
      'snippet-teaching': {
        title: 'Choreography for the younger department',
        meta: 'LMIPA · dance teacher',
        description:
          'Original choreography created for the younger departments of Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  de: {
    categories: { all: 'Alle', portrait: 'Porträt', stage: 'Bühne', studio: 'Studio' },
    photos: {
      headshot: { caption: 'Casting-Headshot', alt: 'Casting-Headshot von Fauve Willems' },
      'portrait-soft': { caption: 'Editorial-Porträt', alt: 'Porträt in zarten Rosatönen' },
      stage1: {
        caption: 'Abschlusspräsentation LMIPA',
        alt: 'Fauve im Sprung auf einer großen Bühne',
      },
      'dance-studio': { caption: 'Modern · Studioarbeit', alt: 'Moderner Tanz im Studio' },
      ballet: { caption: 'Ballett an der Stange', alt: 'Ballett an der Stange' },
      teaching: {
        caption: 'Tanzdozentin, LMIPA',
        alt: 'Fauve unterrichtet eine Gruppe von Studierenden',
      },
      stage2: { caption: 'Ensemble · Arena-Show', alt: 'Ensemble auf einer Arenabühne' },
    },
    reels: {
      'reel-dance': { title: 'Commercial-/Jazz-Reel', meta: '0:58 · Choreografie' },
      'reel-voice': { title: 'Gesangs-Reel · Sopran', meta: '1:12 · Musical & Pop' },
      'reel-acting': { title: 'Schauspiel-Reel · Monolog', meta: '1:04 · Theater & Kamera' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Abschlusspräsentation LMIPA',
        meta: 'Dance Captain · Solo',
        description:
          'Ausschnitt aus der jährlichen Abschlusspräsentation, bei der ich als Dance Captain für verschiedene Stücke verantwortlich war.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · Nov 2025',
        description:
          'Ensemblearbeit aus dem Discobal in AFAS Live, mit Live-Band und kompletter Besetzung.',
      },
      'snippet-teaching': {
        title: 'Choreografie für die jüngere Abteilung',
        meta: 'LMIPA · Tanzdozentin',
        description:
          'Eigene Choreografie für die jüngeren Abteilungen des Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  fr: {
    categories: { all: 'Tout', portrait: 'Portrait', stage: 'Scène', studio: 'Studio' },
    photos: {
      headshot: { caption: 'Photo de casting', alt: 'Photo de casting de Fauve Willems' },
      'portrait-soft': { caption: 'Portrait editorial', alt: 'Portrait aux tons rosés doux' },
      stage1: {
        caption: 'Spectacle annuel LMIPA',
        alt: 'Fauve en saut sur une grande scène',
      },
      'dance-studio': { caption: 'Moderne · travail en studio', alt: 'Danse moderne en studio' },
      ballet: { caption: 'Ballet à la barre', alt: 'Ballet à la barre' },
      teaching: {
        caption: 'Professeure de danse, LMIPA',
        alt: 'Fauve donne un cours de danse à un groupe d’élèves',
      },
      stage2: { caption: 'Ensemble · spectacle en arène', alt: 'Ensemble sur une scène d’arène' },
    },
    reels: {
      'reel-dance': { title: 'Reel commercial / jazz', meta: '0:58 · chorégraphie' },
      'reel-voice': { title: 'Reel chant · soprano', meta: '1:12 · musical & pop' },
      'reel-acting': { title: 'Reel jeu · monologue', meta: '1:04 · théâtre & caméra' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Spectacle annuel LMIPA',
        meta: 'Dance captain · solo',
        description:
          "Extrait du spectacle annuel où j'étais dance captain, responsable de plusieurs pièces.",
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · nov. 2025',
        description:
          'Travail d’ensemble du Discobal à AFAS Live, avec orchestre live et distribution complète.',
      },
      'snippet-teaching': {
        title: 'Chorégraphie pour la section jeune',
        meta: 'LMIPA · professeure de danse',
        description:
          'Chorégraphie originale créée pour les sections plus jeunes du Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  es: {
    categories: { all: 'Todo', portrait: 'Retrato', stage: 'Escenario', studio: 'Estudio' },
    photos: {
      headshot: { caption: 'Foto de casting', alt: 'Foto de casting de Fauve Willems' },
      'portrait-soft': { caption: 'Retrato editorial', alt: 'Retrato en tonos rosas suaves' },
      stage1: {
        caption: 'Presentación anual LMIPA',
        alt: 'Fauve saltando en un gran escenario',
      },
      'dance-studio': { caption: 'Moderno · trabajo de estudio', alt: 'Danza moderna en el estudio' },
      ballet: { caption: 'Ballet en la barra', alt: 'Ballet en la barra' },
      teaching: {
        caption: 'Profesora de danza, LMIPA',
        alt: 'Fauve dando clase de danza a un grupo de alumnos',
      },
      stage2: { caption: 'Ensemble · show en arena', alt: 'Ensemble en un escenario de arena' },
    },
    reels: {
      'reel-dance': { title: 'Reel commercial / jazz', meta: '0:58 · coreografía' },
      'reel-voice': { title: 'Reel de canto · soprano', meta: '1:12 · musical y pop' },
      'reel-acting': { title: 'Reel de interpretación · monólogo', meta: '1:04 · teatro y cámara' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Presentación anual LMIPA',
        meta: 'Dance captain · solo',
        description:
          'Fragmento de la presentación anual en la que fui dance captain y responsable de varias piezas.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · nov. 2025',
        description:
          'Trabajo de ensemble del Discobal en AFAS Live, con banda en directo y reparto completo.',
      },
      'snippet-teaching': {
        title: 'Coreografía para el grupo joven',
        meta: 'LMIPA · profesora de danza',
        description:
          'Coreografía propia creada para los grupos más jóvenes del Lucia Marthas Institute for Performing Arts.',
      },
    },
  },
}
